// "Share result": an image of the round's result (1200×630, the size link previews use) with
// the title's colors and a link back to it, ready to paste in a team chat. The same file lives
// in every WorkBrawl title; only the data passed in changes.
//
// The image is drawn in the browser and never uploaded: phones get the system share sheet,
// computers a preview with copy and download. The link carries UTM tags so Analytics can tell
// how many visits the shared results bring (event "result_shared" counts the shares).

import { track } from "./analytics";
import { openDialog } from "./dialog";
import { h } from "./dom";
import { LANG } from "./i18n";

export interface ResultCard {
  /** Display name and path segment of the title, e.g. "GuessBrawl" and "guessbrawl". */
  title: string;
  slug: string;
  /** Background color of the card: the title's theme color. */
  accent: string;
  /**
   * Neutral line about the result (the image is seen by people who weren't there, so never
   * "You win"). Cooperative titles can leave it out for "Team result".
   */
  headline?: string;
  /** Competitive titles: up to three places. */
  podium?: { name: string; score: string; color: string }[];
  /** Cooperative titles: the team's score and an optional detail line. */
  team?: { score: string; detail?: string };
  /** Number of people in the room and the round length in minutes (0: no time limit). */
  people: number;
  minutes: number;
  /** Rounds the result covers, when it adds up a series of them. Omitted = 1. */
  rounds?: number;
}

const TEXT = {
  en: {
    share: "Share result",
    dialogTitle: "Share your result",
    hint: "Paste it in your team chat and invite others to try it.",
    copyImage: "Copy image",
    download: "Download",
    copyText: "Copy text and link",
    copied: "Copied!",
    close: "Close",
    failed: "Couldn't create the image",
    tagline: "Quick team challenges for your work break",
    message: (headline: string, title: string) => `${headline} — ${title} on WorkBrawl. Try it with your team:`,
    teamHeadline: "Team result",
    meta: (people: number, minutes: number, rounds: number) =>
      `${people} ${people === 1 ? "person" : "people"}` +
      (rounds > 1
        ? ` · ${rounds} rounds${minutes > 0 ? ` of ${minutes} min` : ""}`
        : minutes > 0
          ? ` · ${minutes}-minute round`
          : ""),
    imageAlt: "Image of the result",
  },
  es: {
    share: "Compartir resultado",
    dialogTitle: "Comparte tu resultado",
    hint: "Pégalo en el chat de tu equipo e invita a otros a probarlo.",
    copyImage: "Copiar imagen",
    download: "Descargar",
    copyText: "Copiar texto y enlace",
    copied: "¡Copiado!",
    close: "Cerrar",
    failed: "No se pudo crear la imagen",
    tagline: "Retos rápidos en equipo para tu pausa activa",
    message: (headline: string, title: string) => `${headline} — ${title} en WorkBrawl. Pruébalo con tu equipo:`,
    teamHeadline: "Resultado del equipo",
    meta: (people: number, minutes: number, rounds: number) =>
      `${people} ${people === 1 ? "persona" : "personas"}` +
      (rounds > 1
        ? ` · ${rounds} rondas${minutes > 0 ? ` de ${minutes} min` : ""}`
        : minutes > 0
          ? ` · ronda de ${minutes} minutos`
          : ""),
    imageAlt: "Imagen del resultado",
  },
}[LANG === "es" ? "es" : "en"];

const WIDTH = 1200;
const HEIGHT = 630;
const DISPLAY_FONT = '"Lilita One", system-ui, sans-serif';
const BODY_FONT = 'ui-rounded, "SF Pro Rounded", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const INK = "#1d1b24";
const MUTED = "#6b6878";
const MEDALS = ["#f5b70a", "#a7b0bd", "#d08a4c"];
const COPIED_MS = 2000;

/** The button for a results screen. `card` is read when clicked, so it sees the final data. */
export function shareButton(card: () => ResultCard, className = "btn"): HTMLButtonElement {
  const button = h("button", { type: "button", class: `${className} share-result` }, `↗︎ ${TEXT.share}`);
  button.addEventListener("click", async () => {
    const label = button.textContent;
    button.disabled = true;
    try {
      await shareResult(card());
    } catch (err) {
      console.error("[WorkBrawl] result image failed", err);
      button.textContent = TEXT.failed;
      setTimeout(() => (button.textContent = label), COPIED_MS);
    } finally {
      button.disabled = false;
    }
  });
  return button;
}

export async function shareResult(card: ResultCard): Promise<void> {
  const link = `https://workbrawl.com/${card.slug}/?utm_source=share&utm_medium=result_card&utm_campaign=${card.slug}`;
  const headline = card.headline ?? `${TEXT.teamHeadline}: ${card.team?.score ?? ""}`;
  const text = `${TEXT.message(headline, card.title)} ${link}`;
  const blob = await toBlob(await drawCard(card));
  const file = new File([blob], `${card.slug}-result.png`, { type: "image/png" });

  // Phones and tablets: the system share sheet, straight into WhatsApp, Slack, Teams…
  const touch = matchMedia("(pointer: coarse)").matches;
  if (touch && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], text });
      track("result_shared", { method: "native", title: card.slug });
    } catch {
      // Sheet dismissed: nothing to do.
    }
    return;
  }
  openPreview(card, blob, file, text);
}

/** Computers: the image, plus copy and download. */
function openPreview(card: ResultCard, blob: Blob, file: File, text: string): void {
  const url = URL.createObjectURL(blob);
  const image = h("img", { src: url, alt: TEXT.imageAlt, width: WIDTH, height: HEIGHT, class: "share-preview" });
  const actions: HTMLElement[] = [];

  const flash = (button: HTMLButtonElement, label: string) => {
    button.textContent = TEXT.copied;
    setTimeout(() => (button.textContent = label), COPIED_MS);
  };

  if (typeof ClipboardItem !== "undefined" && navigator.clipboard && "write" in navigator.clipboard) {
    const copyImage = h("button", { type: "button", class: "btn btn-primary" }, TEXT.copyImage);
    copyImage.addEventListener("click", async () => {
      try {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        flash(copyImage, TEXT.copyImage);
        track("result_shared", { method: "copy_image", title: card.slug });
      } catch {
        copyImage.disabled = true;
      }
    });
    actions.push(copyImage);
  }

  const download = h("a", { class: "btn", href: url, download: file.name }, TEXT.download);
  download.addEventListener("click", () => track("result_shared", { method: "download", title: card.slug }));
  actions.push(download);

  const copyText = h("button", { type: "button", class: "btn" }, TEXT.copyText);
  copyText.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(text);
      flash(copyText, TEXT.copyText);
      track("result_shared", { method: "copy_text", title: card.slug });
    } catch {
      copyText.disabled = true;
    }
  });
  actions.push(copyText);

  const close = h(
    "button",
    { type: "button", class: "share-close", "aria-label": TEXT.close, title: TEXT.close },
    "\u00d7",
  );
  const dialog = openDialog(
    "share-dialog",
    close,
    h("h2", { class: "dialog-title" }, TEXT.dialogTitle),
    image,
    h("p", { class: "muted" }, TEXT.hint),
    h("div", { class: "dialog-actions" }, ...actions),
  );
  close.addEventListener("click", () => dialog.close());
  // Start on the main action rather than on the close button.
  actions[0]?.focus();
  dialog.el.addEventListener("close", () => URL.revokeObjectURL(url));
}

async function drawCard(card: ResultCard): Promise<HTMLCanvasElement> {
  // The display font is self-hosted by each title; make sure it is ready before drawing.
  try {
    await document.fonts.load(`60px ${DISPLAY_FONT}`);
  } catch {
    // Falls back to the system font.
  }
  const icon = await loadImage(`/${card.slug}/icon-192.png`).catch(() => null);

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No 2D context");

  // Background: the title's color, darker towards the bottom, with two soft circles.
  const bg = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
  bg.addColorStop(0, card.accent);
  bg.addColorStop(1, shade(card.accent, -0.35));
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
  ctx.fillStyle = "rgba(255,255,255,0.07)";
  circle(ctx, WIDTH - 90, 70, 190);
  circle(ctx, 120, HEIGHT + 40, 160);

  // Header: icon, title, site.
  let x = 64;
  if (icon) {
    ctx.save();
    roundRect(ctx, x, 44, 92, 92, 22);
    ctx.clip();
    ctx.drawImage(icon, x, 44, 92, 92);
    ctx.restore();
    x += 116;
  }
  ctx.fillStyle = "#ffffff";
  ctx.textBaseline = "alphabetic";
  ctx.font = `64px ${DISPLAY_FONT}`;
  ctx.fillText(card.title, x, 104);
  ctx.font = `600 26px ${BODY_FONT}`;
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.fillText("workbrawl.com", x + 2, 134);

  // Date, top right.
  ctx.textAlign = "right";
  ctx.font = `600 24px ${BODY_FONT}`;
  ctx.fillText(
    new Intl.DateTimeFormat(LANG, { day: "numeric", month: "long", year: "numeric" }).format(new Date()),
    WIDTH - 64,
    100,
  );
  ctx.textAlign = "left";

  // Result panel.
  const panel = { x: 64, y: 172, w: WIDTH - 128, h: 340 };
  ctx.fillStyle = "rgba(0,0,0,0.18)";
  roundRect(ctx, panel.x, panel.y + 8, panel.w, panel.h, 32);
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  roundRect(ctx, panel.x, panel.y, panel.w, panel.h, 32);
  ctx.fill();

  const inner = panel.x + 48;
  const innerW = panel.w - 96;
  ctx.fillStyle = INK;
  ctx.font = `48px ${DISPLAY_FONT}`;
  ctx.fillText(fit(ctx, card.headline ?? TEXT.teamHeadline, innerW), inner, panel.y + 76);

  if (card.podium && card.podium.length > 0) {
    card.podium.slice(0, 3).forEach((entry, i) => {
      const y = panel.y + 104 + i * 62;
      // Place medal.
      ctx.fillStyle = MEDALS[i]!;
      circle(ctx, inner + 24, y + 26, 24);
      ctx.fillStyle = "#ffffff";
      ctx.font = `28px ${DISPLAY_FONT}`;
      ctx.textAlign = "center";
      ctx.fillText(String(i + 1), inner + 24, y + 36);
      // Avatar with the person's color.
      ctx.fillStyle = entry.color;
      circle(ctx, inner + 86, y + 26, 24);
      ctx.fillStyle = "#ffffff";
      ctx.font = `700 24px ${BODY_FONT}`;
      ctx.fillText(([...entry.name][0] ?? "?").toUpperCase(), inner + 86, y + 35);
      // Score, then the name in the space left.
      ctx.textAlign = "right";
      ctx.fillStyle = INK;
      ctx.font = `32px ${DISPLAY_FONT}`;
      ctx.fillText(entry.score, inner + innerW, y + 38);
      const scoreW = ctx.measureText(entry.score).width;
      ctx.textAlign = "left";
      ctx.font = `${i === 0 ? 800 : 700} 32px ${BODY_FONT}`;
      ctx.fillText(fit(ctx, entry.name, innerW - 140 - scoreW), inner + 128, y + 38);
    });
  } else if (card.team) {
    ctx.fillStyle = card.accent;
    ctx.font = `96px ${DISPLAY_FONT}`;
    ctx.fillText(fit(ctx, card.team.score, innerW), inner, panel.y + 200);
    if (card.team.detail) {
      ctx.fillStyle = MUTED;
      ctx.font = `600 30px ${BODY_FONT}`;
      ctx.fillText(fit(ctx, card.team.detail, innerW), inner, panel.y + 254);
    }
  }

  ctx.fillStyle = MUTED;
  ctx.font = `600 24px ${BODY_FONT}`;
  ctx.fillText(TEXT.meta(card.people, card.minutes, card.rounds ?? 1), inner, panel.y + panel.h - 28);

  // Footer: tagline and the address to visit.
  ctx.fillStyle = "#ffffff";
  ctx.font = `700 28px ${BODY_FONT}`;
  ctx.fillText(TEXT.tagline, 64, HEIGHT - 50);
  const address = `workbrawl.com/${card.slug}`;
  ctx.font = `30px ${DISPLAY_FONT}`;
  const pillW = ctx.measureText(address).width + 48;
  ctx.fillStyle = "#ffffff";
  roundRect(ctx, WIDTH - 64 - pillW, HEIGHT - 92, pillW, 60, 30);
  ctx.fill();
  ctx.fillStyle = shade(card.accent, -0.2);
  ctx.textAlign = "center";
  ctx.fillText(address, WIDTH - 64 - pillW / 2, HEIGHT - 51);
  ctx.textAlign = "left";

  return canvas;
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))), "image/png"),
  );
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Could not load ${src}`));
    image.src = src;
  });
}

/** Cuts `text` with an ellipsis so it fits in `width` with the current font. */
function fit(ctx: CanvasRenderingContext2D, text: string, width: number): string {
  if (ctx.measureText(text).width <= width) return text;
  const chars = [...text];
  while (chars.length > 1 && ctx.measureText(`${chars.join("")}…`).width > width) chars.pop();
  return `${chars.join("")}…`;
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Lightens (amount > 0) or darkens (amount < 0) a #rrggbb color. */
function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const channel = (shift: number) => {
    const c = (n >> shift) & 255;
    const v = amount < 0 ? c * (1 + amount) : c + (255 - c) * amount;
    return Math.round(Math.max(0, Math.min(255, v)));
  };
  return `#${[16, 8, 0].map((s) => channel(s).toString(16).padStart(2, "0")).join("")}`;
}
